'use client';

import { useCallback } from 'react';

import Image from 'next/image';
import { useRouter } from 'next/navigation';

import { LeadCaptureForm } from '@selenos/leads/components/lead-capture-form';

import { useExperiment } from '~/lib/analytics/experiment-context';
import { useTrackingParams } from '~/lib/tracking/use-tracking-params';

import { useVerticalConfig } from './use-vertical-config';

interface InlineLeadFormProps {
  citySlug: string;
  verticalSlug: string;
  locale: string;
  submitAction: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
  partialSaveAction?: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
}

/**
 * Inline lead capture form section for landing page experiments.
 *
 * Resolves the vertical config and form schema on the client side
 * to avoid passing Zod objects across the server/client boundary.
 */
export function InlineLeadForm({
  citySlug,
  verticalSlug,
  locale,
  submitAction,
  partialSaveAction,
}: InlineLeadFormProps) {
  const router = useRouter();
  const experiment = useExperiment();
  const { verticalConfig, customFormFields, phonePlaceholder, requirePhone } =
    useVerticalConfig(verticalSlug);

  // UTM + click-ids + _fbp/_fbc cookies + experiment variant (shared hook).
  const tracking = useTrackingParams(experiment?.variant);

  const handleSuccess = useCallback(
    (leadId?: string) => {
      router.push(
        `/${locale}/${verticalSlug}/${citySlug}/thank-you${leadId ? `?leadId=${leadId}` : ''}`,
      );
    },
    [locale, verticalSlug, citySlug, router],
  );

  if (!verticalConfig) {
    return null;
  }

  return (
    <section
      id="apply-form"
      className="w-full px-4 py-16 sm:py-20"
      style={{ backgroundColor: 'var(--landing-secondary)' }}
    >
      <div className="mx-auto max-w-5xl">
        <h2 className="mb-2 text-center text-3xl font-black text-white sm:text-4xl">
          Apply Now
        </h2>
        <p className="mb-8 text-center text-white/60">
          Fill out the form below. It takes less than 5 minutes.
        </p>

        <div className="flex flex-col items-stretch gap-8 lg:flex-row">
          {/* Form companion image (desktop only) */}
          <div className="hidden overflow-hidden rounded-2xl lg:block lg:w-2/5">
            <Image
              src="/images/landing/drivers/form-driver-ready.webp"
              alt="A Rovvo driver ready to start her shift"
              width={600}
              height={800}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>

          {/* Form */}
          <div className="flex-1">
            <div className="bg-card rounded-2xl border border-white/10 p-6 sm:p-8">
              <LeadCaptureForm
                verticalConfig={verticalConfig}
                citySlug={citySlug}
                verticalSlug={verticalSlug}
                customFormFields={customFormFields}
                submitAction={submitAction}
                partialSaveAction={partialSaveAction}
                onSuccess={handleSuccess}
                tracking={tracking}
                phonePlaceholder={phonePlaceholder}
                requirePhone={requirePhone}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
